import { GOVERNORATES, useI18n, type Locale } from '@ecom/i18n';
import type { ReactNode } from 'react';

import { Select, type SelectProps } from './Select';

export interface GovernorateSelectProps extends Omit<
  SelectProps,
  'options' | 'value' | 'onChange'
> {
  readonly value: string;
  readonly onChange: (code: string) => void;
}

/**
 * The structured governorate field required by PDF §27 — one of the 24
 * official values, never free text. Label and option order follow the
 * active locale (French name in `fr`, Arabic name in `ar`); the underlying
 * `value` is always the stable code (e.g. `'TUN'`), so it round-trips
 * correctly regardless of which language it was picked in.
 */
export function GovernorateSelect({ value, onChange, ...rest }: GovernorateSelectProps): ReactNode {
  const { t, locale } = useI18n();

  return (
    <Select
      {...rest}
      value={value}
      options={GOVERNORATES.map((g) => ({ value: g.code, label: nameFor(g, locale) }))}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      aria-label={rest['aria-label'] ?? t('governorate.label')}
    />
  );
}

export interface DelegationOption {
  readonly value: string;
  readonly label: string;
}

export interface DelegationSelectProps extends Omit<SelectProps, 'options' | 'value' | 'onChange'> {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /**
   * Supplied by the caller, scoped to the currently selected governorate.
   * Delegations are NOT bundled in @ecom/i18n: there is no authoritative
   * source for the full ~260-entry list in the project's provided
   * documents, and hardcoding one from memory risks a wrong delivery
   * address, which is an operational failure, not a cosmetic one. This
   * component is built now so Phase 1/2 catalogue work only needs to supply
   * the verified dataset, not redesign the field.
   */
  readonly delegations: readonly DelegationOption[];
}

export function DelegationSelect({
  value,
  onChange,
  delegations,
  ...rest
}: DelegationSelectProps): ReactNode {
  const { t } = useI18n();

  return (
    <Select
      {...rest}
      value={value}
      options={delegations}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      aria-label={rest['aria-label'] ?? t('delegation.label')}
    />
  );
}

function nameFor(governorate: { nameFr: string; nameAr: string }, locale: Locale): string {
  return locale === 'ar' ? governorate.nameAr : governorate.nameFr;
}
